"use client";
/**
 * Watches which physical screen the window is on and, when it changes (you drag
 * the window from the laptop display to the external monitor, or dock/undock),
 * switches to the Display Profile associated with that screen — so the layout
 * follows the monitor without touching the dropdown.
 *
 * There is no reliable "moved to another screen" event, so we poll
 * `window.screen.{width,height}` (cheap) and also listen to `resize` (which
 * fires on many monitor moves). A match triggers a profile switch + reload so
 * the grid / resizable panels remount against the new profile's slots. Off by
 * default; enabled from Settings → Pages (`isAutoDetect`).
 */
import { useEffect } from "react";
import {
  autoApplyForCurrentScreen, isAutoDetect, PROFILE_APPLIED_EVENT, screenSignature,
} from "@/lib/display/profiles";

export function ProfileAutoDetect() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    let last = screenSignature();

    // On mount, apply the match for the current screen without a reload (initial
    // load already handles this pre-render; this covers a client-side nav where
    // the gate didn't re-run). If it changes the active profile, tell mounted
    // panes to re-read via the applied event.
    try { if (autoApplyForCurrentScreen()) window.dispatchEvent(new CustomEvent(PROFILE_APPLIED_EVENT)); } catch { /* ignore */ }

    const check = () => {
      if (!isAutoDetect()) return;
      const sig = screenSignature();
      if (!sig || (last && sig.w === last.w && sig.h === last.h)) return; // screen unchanged
      last = sig;
      // Goes through autoApply (not switchProfile) so an automatic move never
      // records a hand-pick override, and a hand-pick made on the screen we just
      // arrived at is still respected.
      if (autoApplyForCurrentScreen()) window.location.reload();
    };

    const iv = setInterval(check, 1500);
    window.addEventListener("resize", check);
    return () => {
      clearInterval(iv);
      window.removeEventListener("resize", check);
    };
  }, []);

  return null;
}
